import { useState, useRef } from 'react';
import { Calendar, ChevronDown, Plus } from 'lucide-react';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useMultiGrid } from '../../hooks/useMultiGrid';

interface YearSelectorProps {
  className?: string;
  showGridCount?: boolean;
  onStartNewYear?: () => void;
}

export function YearSelector({ className = '', showGridCount = true, onStartNewYear }: YearSelectorProps) {
  const { workingYear, setWorkingYear, availableYears, isLoading } = useAcademicYear();
  const { allGrids } = useMultiGrid();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Count grids per year
  const getGridCountForYear = (year: number): number => {
    return allGrids.filter(g => g.year === year).length;
  };

  // Get next year for "Start New Year" action
  const getNextYear = (): number => {
    const maxYear = Math.max(...availableYears, workingYear);
    return maxYear + 1;
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse bg-ink-200 h-10 w-32 rounded ${className}`}></div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-ink-300 rounded-lg hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500 transition-colors"
      >
        <Calendar size={18} className="text-navy-600" />
        <span className="font-mono tabular-nums font-semibold text-ink-900">{workingYear}</span>
        {showGridCount && (
          <span className="px-2 py-0.5 bg-navy-100 text-navy-700 text-xs rounded-full">
            {getGridCountForYear(workingYear)} grids
          </span>
        )}
        <ChevronDown
          size={16}
          className={`text-ink-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && buttonRef.current && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown - using fixed positioning to escape overflow:hidden parents */}
          <div
            className="fixed w-64 bg-white border border-ink-200 rounded-lg shadow-lg z-50"
            style={{
              top: buttonRef.current.getBoundingClientRect().bottom + 4,
              left: buttonRef.current.getBoundingClientRect().left,
            }}
          >
            {/* Year List */}
            <div className="py-1 max-h-60 overflow-y-auto">
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => {
                    setWorkingYear(year);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-ink-50 ${
                    year === workingYear ? 'bg-navy-50 text-navy-700' : 'text-ink-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className={year === workingYear ? 'text-navy-600' : 'text-ink-400'} />
                    <span className="font-mono tabular-nums font-medium">{year}</span>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    year === workingYear
                      ? 'bg-navy-100 text-navy-700'
                      : 'bg-ink-100 text-ink-500'
                  }`}>
                    {getGridCountForYear(year)} grids
                  </span>
                </button>
              ))}

              {availableYears.length === 0 && (
                <div className="px-4 py-3 text-sm text-ink-500 text-center">
                  No years with grids found
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-ink-200" />

            {/* Actions */}
            <div className="py-1">
              {onStartNewYear && (
                <button
                  onClick={() => {
                    onStartNewYear();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy-600 hover:bg-navy-50"
                >
                  <Plus size={16} />
                  <span>Start New Year ({getNextYear()})</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default YearSelector;
